import { useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { FormField } from '../../../../components/FormField/FormField';
import { SingleSelect } from '../../../../components/SingleSelect/SingleSelect';
import { Typography } from '../../../../components/Typography/Typography';
import { SelectItem } from '../../../../types';
import { useWorkflows } from '../../../../queries/hooks/useWorkflows';
import { byWorkflowOrder } from '../../../../utils/workflowOrder';
import { IUserFileds } from './UserModal';
import { WorkflowStagesIndicator } from './WorkflowStagesIndicator';

/**
 * Approval Workflow picker. Rendered for timesheet submitters (OP or isOp OP Manager). The selected
 * workflow drives the whole approval pipeline and which relation pickers (Manager / SVP Manager) are
 * shown; the backend derives isAssetServices from it, so there is no separate "Skip Deal Owner" switch.
 */
export function WorkflowField() {
  const { colors } = useTheme();
  const { data: workflows } = useWorkflows();
  const { setFieldValue, values, setFieldTouched, touched, errors } = useFormikContext<IUserFileds>();

  const workflowOptions: SelectItem[] = useMemo(() => {
    if (!workflows) return [];
    return [...workflows]
      .sort(byWorkflowOrder)
      .map((workflow) => ({ id: workflow.id, value: workflow.name }));
  }, [workflows]);

  return (
    <FormField
      label={'Approver workflow'}
      error={touched.workflow ? (errors.workflow as string) : ''}
      labelStyle={{ color: colors.text.caption }}
    >
      <Typography variant='srOnly' color={colors.text.caption}>
        Select workflow *
      </Typography>
      <SingleSelect
        listboxStyle={{ height: '195px' }}
        options={workflowOptions}
        value={values?.workflow ?? null}
        onChange={(_, option) => {
          setFieldValue('workflow', option);
        }}
        onBlur={() => {
          setFieldTouched('workflow');
        }}
        disablePortal
        fieldPlaceholder='Select workflow'
      />
      <Typography variant='caption' color={colors.text.secondary}>
        The approval steps each timesheet goes through.
      </Typography>
      <WorkflowStagesIndicator />
    </FormField>
  );
}
